import React from 'react';
import './review.css';

const OPTION_CONTROLS = new Set(['select', 'dropdown', 'radio', 'checkbox']);

function slug(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function questionLabel(question) {
  return String(
    question.label ||
    question.fieldLabel ||
    question.fieldName ||
    question.name ||
    ''
  ).trim();
}

function questionId(question) {
  return `review-${question.id ?? question.fieldKey ?? (slug(questionLabel(question)) || 'field')}`;
}

function optionInputId(baseId, option, index) {
  if (index === 0) return baseId;
  return `${baseId}-${slug(option) || index}`;
}

function controlKind(question) {
  const type = String(question.fieldType || '').toLowerCase();
  const key = String(question.fieldKey || questionLabel(question) || '').toLowerCase();

  if (type === 'email' || key.includes('email')) return 'email';
  if (type === 'tel' || type === 'phone' || key.includes('phone')) return 'tel';
  if (type === 'textarea' || type === 'text area') return 'textarea';
  if (type === 'date') return 'date';
  if (type === 'date time' || type === 'datetime' || type === 'datetime-local') return 'datetime-local';
  if (type === 'time') return 'time';
  if (type === 'number') return 'number';
  if (type === 'radio') return 'radio';
  if (type === 'checkbox') return 'checkbox';
  if (type === 'multiselect' || type === 'dropdown' || type === 'select' || type === 'custom') {
    return question.options?.length ? 'select' : 'text';
  }
  return 'text';
}

function usesOptions(kind) {
  return OPTION_CONTROLS.has(kind) || kind === 'select';
}

function isWideQuestion(kind) {
  return kind === 'textarea' || kind === 'radio' || kind === 'checkbox';
}

function isPlainTextField(question) {
  const type = String(question.fieldType || '').toLowerCase();
  return type === 'text' || type === 'textfield';
}

function stripDigits(value) {
  return String(value || '').replace(/\d/g, '');
}

function keepDigits(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

function placeholderFor(question, kind) {
  const label = String(questionLabel(question) || 'value').toLowerCase();
  if (kind === 'date') return 'mm/dd/yyyy';
  if (kind === 'select') return `Select ${label}`;
  if (kind === 'textarea') return `Enter ${label}`;
  if (kind === 'email') return 'Enter email';
  return `Enter ${label}`;
}

function ReviewControl({ question }) {
  const id = questionId(question);
  const kind = controlKind(question);
  const options = usesOptions(kind) ? (question.options || []) : [];
  const commonProps = {
    id,
    name: String(question.fieldKey || questionLabel(question) || id),
    required: Boolean(question.required),
  };

  if (kind === 'radio') {
    return (
      <div className="review-options review-options--inline">
        {options.map((option, index) => {
          const optionId = optionInputId(id, option, index);
          return (
            <label className="review-option" htmlFor={optionId} key={option}>
              <input
                {...commonProps}
                id={optionId}
                type="radio"
                value={option}
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (kind === 'checkbox') {
    return (
      <div className="review-options review-options--inline">
        {options.map((option, index) => {
          const optionId = optionInputId(id, option, index);
          return (
            <label className="review-option" htmlFor={optionId} key={option}>
              <input
                id={optionId}
                name={`${commonProps.name}[]`}
                type="checkbox"
                value={option}
                required={false}
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    );
  }

  if (kind === 'textarea') {
    return (
      <textarea
        {...commonProps}
        className="review-control review-control--textarea"
        placeholder={placeholderFor(question, kind)}
      />
    );
  }

  if (kind === 'select') {
    return (
      <select {...commonProps} className="review-control" defaultValue="">
        <option value="">{placeholderFor(question, kind)}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (kind === 'number') {
    return (
      <input
        {...commonProps}
        className="review-control"
        type="number"
        inputMode="numeric"
        placeholder={placeholderFor(question, kind)}
        onKeyDown={(event) => {
          if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-' || event.key === '.') {
            event.preventDefault();
          }
          if (event.key.length === 1 && !/\d/.test(event.key) && !event.ctrlKey && !event.metaKey) {
            event.preventDefault();
          }
        }}
        onInput={(event) => {
          const next = keepDigits(event.target.value);
          if (next !== event.target.value) event.target.value = next;
        }}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData('text');
          if (/^\d*$/.test(pasted)) return;
          event.preventDefault();
          const target = event.target;
          const next = keepDigits(pasted);
          const start = target.selectionStart ?? target.value.length;
          const end = target.selectionEnd ?? target.value.length;
          target.value = `${String(target.value).slice(0, start)}${next}${String(target.value).slice(end)}`;
        }}
      />
    );
  }

  if (isPlainTextField(question)) {
    return (
      <input
        {...commonProps}
        className="review-control"
        type="text"
        inputMode="text"
        placeholder={placeholderFor(question, kind)}
        onKeyDown={(event) => {
          if (/^\d$/.test(event.key)) event.preventDefault();
        }}
        onInput={(event) => {
          const next = stripDigits(event.target.value);
          if (next !== event.target.value) event.target.value = next;
        }}
        onPaste={(event) => {
          const pasted = event.clipboardData.getData('text');
          if (!/\d/.test(pasted)) return;
          event.preventDefault();
          const target = event.target;
          const next = stripDigits(pasted);
          const start = target.selectionStart ?? target.value.length;
          const end = target.selectionEnd ?? target.value.length;
          target.value = `${target.value.slice(0, start)}${next}${target.value.slice(end)}`;
        }}
      />
    );
  }

  return (
    <input
      {...commonProps}
      className="review-control"
      type={kind}
      placeholder={placeholderFor(question, kind)}
    />
  );
}

export default function Review({ fields = [] }) {
  if (!fields.length) {
    return <p className="review-empty">No form fields added</p>;
  }

  return (
    <div className="review-form-card">
      <form className="review-form" aria-label="Form fields preview" onSubmit={(event) => event.preventDefault()}>
        <div className="review-form-grid">
          {fields.map((question) => {
            const kind = controlKind(question);
            const id = questionId(question);
            return (
              <div
                className={`review-question${isWideQuestion(kind) ? ' review-question--wide' : ''}`}
                key={question.id ?? question.fieldKey ?? questionLabel(question)}
              >
                <label className="review-label" htmlFor={id}>
                  {questionLabel(question) || 'Untitled field'}
                  {question.required ? <span className="review-required">*</span> : null}
                </label>
                <ReviewControl question={question} />
              </div>
            );
          })}
        </div>
      </form>
    </div>
  );
}
